import useIsCompactViewport from '@Guidelines/Component/FilterPanel/Hooks/useIsCompactViewport';
import formatEuros from '@UI/Helpers/formatEuros';
import AmountCell from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/AmountCell';
import CostCard from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/CostCard';
import CostListState from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/CostListState';
import CostSummaryCard from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/CostSummaryCard';
import formatQuantity from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Helpers/formatQuantity';
import formatTokens from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Helpers/formatTokens';
import resolveSubjectLabel from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Helpers/resolveSubjectLabel';
import useAiCostBreakdown from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Hooks/useAiCostBreakdown';
import useAiCostSummaries from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Hooks/useAiCostSummaries';
import NameCell from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/NameCell';
import ShowMoreButton from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/ShowMoreButton';
import formatCount from '@UI/Pages/Administration/AiCosts/Helpers/formatCount';
import type AiCostBreakdownRowView from '@UI/Store/AiCosts/Views/AiCostBreakdownRowView';
import { css } from '@emotion/react';
import costTableStyles from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/costTableStyles';
import * as React from 'react';
import { Column, DataTable } from '@verclam/ui-kit';

const ROWS_PER_PAGE = 200;

const cardsStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.625rem;
`;

type Props = {
	readonly accountId: number;
};

const countProcessings = (row: AiCostBreakdownRowView): number => row.audio.count + row.documents.count;

/** Costs per processing step, for the super administrator. Mounted under the query key of its tab. */
const StepCosts: React.FC<Props> = ({ accountId }) => {
	const { list, error, refetch, showMore, isLoadingMore } = useAiCostBreakdown(
		accountId,
		'steps',
		ROWS_PER_PAGE,
	);
	const { total } = useAiCostSummaries(accountId);
	const isCompact = useIsCompactViewport();

	const renderStep = React.useCallback((row: AiCostBreakdownRowView) => {
		const subject = resolveSubjectLabel('steps', row);

		return <NameCell title={subject.title} isMuted={subject.isMuted} />;
	}, []);

	const renderProcessings = React.useCallback(
		(row: AiCostBreakdownRowView) => <AmountCell label={formatCount(countProcessings(row))} />,
		[],
	);

	const renderTokens = React.useCallback(
		(row: AiCostBreakdownRowView) => (
			<AmountCell label={formatTokens(row.quantities?.totalTokens ?? null)} />
		),
		[],
	);

	const renderCalls = React.useCallback(
		(row: AiCostBreakdownRowView) => <AmountCell label={formatCount(row.callCount)} />,
		[],
	);

	const renderCost = React.useCallback(
		(row: AiCostBreakdownRowView) => <AmountCell label={formatEuros(row.costAmount)} />,
		[],
	);

	return (
		<CostCard title="Coûts par étape" subtitle="Réservé au super administrateur.">
			<CostListState list={list} error={error} onRetry={refetch}>
				{(loaded) => (
					<React.Fragment>
						{isCompact ? (
							<div css={cardsStyle}>
								{loaded.list.map((row) => (
									<CostSummaryCard
										key={row.usageLabel ?? 'unattributed'}
										title={resolveSubjectLabel('steps', row).title}
										amount={formatEuros(row.costAmount)}
										lines={[
											`${formatQuantity(countProcessings(row), 'traitement', 'traitements')} · ${formatTokens(row.quantities?.totalTokens ?? null)} jetons · ${formatQuantity(row.callCount ?? 0, 'appel', 'appels')}`,
										]}
									/>
								))}
							</div>
						) : (
							<DataTable
								value={loaded.list}
								ariaLabel="Coûts par étape"
								pt={costTableStyles.pt}
							>
								<Column
									header="Étape"
									body={renderStep}
									footer={total === null ? undefined : <NameCell title="Total" />}
								/>
								<Column
									header="Traitements"
									align="right"
									body={renderProcessings}
									footer={
										total === null ? undefined : (
											<AmountCell
												label={formatCount(total.audio.count + total.documents.count)}
											/>
										)
									}
								/>
								<Column
									header="Jetons"
									align="right"
									body={renderTokens}
									footer={
										total === null ? undefined : (
											<AmountCell
												label={formatTokens(total.quantities?.totalTokens ?? null)}
											/>
										)
									}
								/>
								<Column
									header="Appels"
									align="right"
									body={renderCalls}
									footer={
										total === null ? undefined : (
											<AmountCell label={formatCount(total.callCount)} />
										)
									}
								/>
								<Column
									header="Coût"
									align="right"
									body={renderCost}
									footer={
										total === null ? undefined : (
											<AmountCell label={formatEuros(total.costAmount)} />
										)
									}
								/>
							</DataTable>
						)}
						<ShowMoreButton
							hasMore={loaded.hasMore}
							remainingCount={loaded.remainingCount}
							rows={ROWS_PER_PAGE}
							isLoading={isLoadingMore}
							onShowMore={showMore}
						/>
					</React.Fragment>
				)}
			</CostListState>
		</CostCard>
	);
};

export default StepCosts;
